import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search, X } from "lucide-react";

export function useProductSearch(q) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/product-master/products", { params: { q: q.trim(), page_size: 8 } })
        .then(({ data }) => setRows(data.items || []))
        .catch(() => setRows([])).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return { rows, busy };
}

/** Product Master search → pick one. With `picked` it shows the choice; without it (onPick only) it stays a search box. */
export default function ProductPicker({ picked, setPicked, onPick, testId = "dc-add-asset-product", placeholder = "Search product name, part no. or SKU…", autoFocus = true }) {
  const [q, setQ] = useState("");
  const { rows, busy } = useProductSearch(q);
  const choose = (p) => { if (onPick) { onPick(p); setQ(""); } else setPicked(p); };
  if (picked) {
    return (
      <div className="flex items-center gap-2 rounded border border-sky-200 bg-white px-2 py-1.5 text-[12px] min-w-0" data-testid={`${testId}-picked`}>
        <span className="font-medium min-w-0 truncate">{picked.product_name}</span>
        <span className="font-mono text-[10px] text-slate-500 truncate">{picked.oem_part_number || picked.sku || ""}</span>
        <button type="button" className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setPicked(null)} aria-label="Clear product" data-testid={`${testId}-unpick`}><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="space-y-1 min-w-0">
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
        <Input autoFocus={autoFocus} placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-7 text-[12px]" data-testid={`${testId}-search`} />
        {busy && <Loader2 className="h-3.5 w-3.5 absolute right-2 top-2.5 animate-spin text-slate-400" />}
      </div>
      {rows.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-100" data-testid={`${testId}-results`}>
          {rows.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => choose(p)} className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-sky-50 flex gap-2" data-testid={`${testId}-${p.id}`}>
                <span className="min-w-0 truncate">{p.product_name}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-500">{[p.brand, p.oem_part_number || p.sku].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
